import React, { ReactNode, useCallback, useEffect, useState } from "react";
import { Box, TypographyProps } from "@mui/material";
import {
  AccountSelectGroup,
  AccountsApiListAndSearchAllAccountsRequest,
  AccountsApiSelectGroupsIdRequest,
} from "@rentablo/api/apiv1";
import { useCreateOrUpdateSelectGroup } from "@rentablo/api/apiv1";
import { DepotsSelectorModal } from "@features/selectDepots/components/DepotsSelectorModal";
import { usePublicOrPrivateAccounts } from "@features/sharedPortfolio/hooks/usePublicOrPrivateAccounts";
import { filterOutHiddenAccounts } from "../../hooks/useSelectGroupsForId";
import { DepotsSelectorButton } from "./components/DepotsSelectorButton";
import { DepotsSelectorBody } from "./components/DepotsSelectorBody";

export type Props = {
  selectedGroup?: AccountSelectGroup;
  accountParams?: AccountsApiListAndSearchAllAccountsRequest;
  selectedGroupParams: AccountsApiSelectGroupsIdRequest;
  color?: string;
  showOptions?: boolean;
  onShowClick?: (val: boolean) => void;
  textKeys?: {
    allDepots: string;
    amountDepots: string;
    noDepots: string;
  };
  icon?: ReactNode;
  onSave?: () => void;
  onBack?: () => void;
} & TypographyProps;

export const DepotsSelector = ({
  selectedGroup,
  accountParams,
  selectedGroupParams,
  color = "text.primary",
  textKeys = {
    allDepots: "depotsSelector.allDepots",
    amountDepots: "depotsSelector.amountDepots",
    noDepots: "depotsSelector.noDepots",
  },
  icon,
  onSave,
  showOptions,
  onBack,
  onShowClick,
  ...rest
}: Props) => {
  const { accounts } = usePublicOrPrivateAccounts(accountParams);
  const { mutateAsync } = useCreateOrUpdateSelectGroup();
  const [isDepotSelectorModalOpen, setIsDepotSelectorModalOpen] =
    useState(false);
  const [checkedPortfolios, setCheckedPortfolios] = useState<number[]>(
    selectedGroup?.accountIds || []
  );
  const visibleAccounts = filterOutHiddenAccounts(accounts);
  const isOutsideModal = Boolean(onBack) && Boolean(onShowClick);

  const saveSelectedAccounts = useCallback(async () => {
    const payload = {
      name: selectedGroupParams.id,
      accountIds: checkedPortfolios,
    };
    await mutateAsync({ createOrUpdateAccountSelectGroupParams: payload });
    onSave?.();
  }, [checkedPortfolios, mutateAsync, onSave, selectedGroupParams.id]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- sync from prop
    setCheckedPortfolios(selectedGroup?.accountIds || []);
  }, [selectedGroup?.accountIds]);

  return (
    <Box color={color}>
      {(!isOutsideModal || !showOptions) && (
        <DepotsSelectorButton
          accounts={visibleAccounts}
          selectedGroup={selectedGroup}
          accountParams={accountParams}
          selectedGroupParams={selectedGroupParams}
          color={color}
          textKeys={textKeys}
          icon={icon}
          onButtonClick={() => {
            setIsDepotSelectorModalOpen(true);
            onShowClick?.(true);
          }}
          isOpen={isDepotSelectorModalOpen}
          {...rest}
        />
      )}
      {isOutsideModal ? (
        <>
          {showOptions && (
            <DepotsSelectorBody
              // @ts-ignore TODO: backend fix
              accounts={visibleAccounts}
              onConfirm={saveSelectedAccounts}
              checkedPortfolios={checkedPortfolios}
              onSelect={setCheckedPortfolios}
              onClose={onBack}
            />
          )}
        </>
      ) : (
        <DepotsSelectorModal
          // @ts-ignore TODO: backend fix
          accounts={visibleAccounts}
          onConfirm={saveSelectedAccounts}
          isOpen={isDepotSelectorModalOpen}
          checkedPortfolios={checkedPortfolios}
          onSelect={setCheckedPortfolios}
          onClose={() => setIsDepotSelectorModalOpen(false)}
        />
      )}
    </Box>
  );
};
